<script setup>
import { computed, onUnmounted, ref } from 'vue';
import { useToggle, useWindowSize } from '@vueuse/core';
import wootConstants from 'dashboard/constants/globals';
import { useMessageWindow } from 'dashboard/composables/useMessageWindow';
import { useStore } from 'vuex';
import { toast } from 'dashboard/composables/useToast';
import { useI18n } from 'vue-i18n';
import { emitter } from 'shared/helpers/mitt';
import EmailTranscriptModal from './EmailTranscriptModal.vue';
import ResolveAction from '../../buttons/ResolveAction.vue';
import ButtonV4 from 'dashboard/components-next/button/Button.vue';
import DropdownMenu from 'dashboard/components-next/dropdown-menu/DropdownMenu.vue';

import {
  CMD_MUTE_CONVERSATION,
  CMD_SEND_TRANSCRIPT,
  CMD_UNMUTE_CONVERSATION,
} from 'dashboard/helper/commandbar/events';

// No props needed as we're getting currentChat from the store directly
const store = useStore();
const { t } = useI18n();

const [showEmailActionsModal, toggleEmailModal] = useToggle(false);
const [showActionsDropdown, toggleDropdown] = useToggle(false);
const resolveRef = ref(null);

const currentChat = computed(
  () => store.getters['unifiedView/getEffectiveChat']
);

// Celular: o que saiu da linha de cima (adiar, pendente, painel, cronometro)
// mora aqui no ⋮. No desktop a linha continua como sempre.
const { width: windowWidth } = useWindowSize();
const isMobileScreen = computed(() => windowWidth.value < 768);
const {
  hasWindow,
  state: windowState,
  formattedTime,
  WINDOW_STATE,
} = useMessageWindow(currentChat);

const mobileItems = computed(() => {
  if (!isMobileScreen.value) return [];
  const items = [];
  const status = currentChat.value?.status;
  if (status === wootConstants.STATUS_TYPE.OPEN) {
    items.push({
      icon: 'i-lucide-alarm-clock',
      label: t('CONVERSATION.RESOLVE_DROPDOWN.SNOOZE_UNTIL'),
      action: 'snooze',
      value: 'snooze',
    });
    items.push({
      icon: 'i-lucide-pause',
      label: t('CONVERSATION.RESOLVE_DROPDOWN.MARK_PENDING'),
      action: 'pending',
      value: 'pending',
    });
  }
  if (hasWindow.value) {
    const closed = windowState.value === WINDOW_STATE.CLOSED;
    items.push({
      icon: closed ? 'i-lucide-alarm-clock-off' : 'i-lucide-timer',
      label: closed
        ? t('CONVERSATION.HEADER.WINDOW_CLOSED')
        : `${t('CONVERSATION.HEADER.WINDOW_MENU')} · ${formattedTime.value}`,
      action: 'window',
      value: 'window',
    });
  }
  // "Detalhes do contato" saiu daqui: o icone do painel voltou pro cabecalho,
  // e repetir a mesma acao nos dois lugares so alonga o menu.
  return items;
});

const actionMenuItems = computed(() => {
  const items = [];

  if (!currentChat.value.muted) {
    items.push({
      icon: 'i-lucide-volume-off',
      label: t('CONTACT_PANEL.MUTE_CONTACT'),
      action: 'mute',
      value: 'mute',
    });
  } else {
    items.push({
      icon: 'i-lucide-volume-1',
      label: t('CONTACT_PANEL.UNMUTE_CONTACT'),
      action: 'unmute',
      value: 'unmute',
    });
  }

  items.push({
    icon: 'i-lucide-share',
    label: t('CONTACT_PANEL.SEND_TRANSCRIPT'),
    action: 'send_transcript',
    value: 'send_transcript',
  });

  return [...mobileItems.value, ...items];
});

const handleActionClick = async ({ action }) => {
  toggleDropdown(false);

  if (action === 'mute') {
    try {
      await store.dispatch('muteConversation', currentChat.value.id);
      toast.success(t('CONTACT_PANEL.MUTED_SUCCESS'));
    } catch {
      toast.error(t('CONTACT_PANEL.MUTE_FAILED'));
    }
  } else if (action === 'unmute') {
    try {
      await store.dispatch('unmuteConversation', currentChat.value.id);
      toast.success(t('CONTACT_PANEL.UNMUTED_SUCCESS'));
    } catch {
      toast.error(t('CONTACT_PANEL.UNMUTE_FAILED'));
    }
  } else if (action === 'send_transcript') {
    toggleEmailModal();
  } else if (action === 'snooze') {
    resolveRef.value?.openSnoozeModal();
  } else if (action === 'pending') {
    resolveRef.value?.toggleStatus(wootConstants.STATUS_TYPE.PENDING);
  }
};

// These functions are needed for the event listeners
const mute = async () => {
  try {
    await store.dispatch('muteConversation', currentChat.value.id);
    toast.success(t('CONTACT_PANEL.MUTED_SUCCESS'));
  } catch {
    toast.error(t('CONTACT_PANEL.MUTE_FAILED'));
  }
};

const unmute = async () => {
  try {
    await store.dispatch('unmuteConversation', currentChat.value.id);
    toast.success(t('CONTACT_PANEL.UNMUTED_SUCCESS'));
  } catch {
    toast.error(t('CONTACT_PANEL.UNMUTE_FAILED'));
  }
};

emitter.on(CMD_MUTE_CONVERSATION, mute);
emitter.on(CMD_UNMUTE_CONVERSATION, unmute);
emitter.on(CMD_SEND_TRANSCRIPT, toggleEmailModal);

onUnmounted(() => {
  emitter.off(CMD_MUTE_CONVERSATION, mute);
  emitter.off(CMD_UNMUTE_CONVERSATION, unmute);
  emitter.off(CMD_SEND_TRANSCRIPT, toggleEmailModal);
});
</script>

<template>
  <div class="relative flex items-center gap-2 actions--container">
    <ResolveAction
      ref="resolveRef"
      :conversation-id="currentChat.id"
      :status="currentChat.status"
    />
    <div
      v-on-clickaway="() => toggleDropdown(false)"
      class="relative flex items-center group"
    >
      <ButtonV4
        v-tooltip="$t('CONVERSATION.HEADER.MORE_ACTIONS')"
        size="sm"
        variant="ghost"
        color="slate"
        icon="i-lucide-more-vertical"
        class="rounded-md group-hover:bg-n-alpha-2"
        @click="toggleDropdown()"
      />
      <DropdownMenu
        v-if="showActionsDropdown"
        :menu-items="actionMenuItems"
        class="mt-1 ltr:right-0 rtl:left-0 top-full"
        @action="handleActionClick"
      />
    </div>
    <EmailTranscriptModal
      v-if="showEmailActionsModal"
      :show="showEmailActionsModal"
      :current-chat="currentChat"
      @cancel="toggleEmailModal"
    />
  </div>
</template>
